import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  TextField,
  Drawer,
  IconButton,
  Divider,
} from '@mui/material';
import { XIcon, EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { RootState } from '../../utils/redux/redux';
import {
  updateHotelConfig,
  updateReservationSystemSetup,
  setStepStatus,
  setErrors,
  clearErrors,
  clearAllStates,
} from '../../utils/redux/slices/reservationSlice';
import { useSaveAssistant } from '../../hooks/useSaveAssistant';
import { useUser } from '../../hooks/useUser';
import { useAgents } from '../../hooks/useAgents';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';
import ConfirmationModal from '../modals/CofirmationModal';
import { useQuery } from '@tanstack/react-query';
import { getHotelogixSetupApi } from '../../utils/apis';

interface HotelConfigurationDrawerProps {
  open: boolean;
  onClose: () => void;
  store: any;
  fromStoreDetails?: boolean;
}

const DEFAULT_HOTELOGIX_CONFIG = {
  hotelId: '',
  hotelName: '',
  apiUsername: '',
  counterId: '',
  consumerKey: '',
  consumerSecret: '',
  accessSecret: '',
  baseUrl: 'https://crs.hotelogix.net/ws/web/',
  redisHost: 'localhost',
  redisPort: 6379,
  isEnabled: true,
};

function isSameHotelConfig(a: any, b: any) {
  const keys = Object.keys(
    DEFAULT_HOTELOGIX_CONFIG
  ) as (keyof typeof DEFAULT_HOTELOGIX_CONFIG)[];
  return keys.every(k => a?.[k] === b?.[k]);
}

const HotelConfigurationDrawer: FC<HotelConfigurationDrawerProps> = ({
  open,
  onClose,
  store,
}) => {
  const dispatch = useDispatch();
  const { errors, reservationSystemSetup } = useSelector(
    (state: RootState) => state.reservation
  );

  // Get hotel config for this specific store
  const hotelConfigs = useSelector(
    (state: RootState) =>
      state.reservation.reservationSystemSetup.data.hotelConfigs || {}
  );
  const currentHotelConfig =
    hotelConfigs[store?.id] || DEFAULT_HOTELOGIX_CONFIG;

  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showConsumerSecret, setShowConsumerSecret] = useState(false);
  const [showAccessSecret, setShowAccessSecret] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialConfig, setInitialConfig] = useState(currentHotelConfig);

  const { saveAssistant, isPending: isSaving } = useSaveAssistant();
  const { user } = useUser();
  const { agents } = useAgents();
  const { alert, setAlert, showAlert } = useAlert();

  const { data: hotelogixSetup } = useQuery({
    queryKey: ['hotelogixSetup', store?.id],
    queryFn: () => getHotelogixSetupApi(store?.id, user?.tenantName),
    enabled: !!store?.id && !!user?.tenantName,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  useEffect(() => {
    if (open && hotelogixSetup?.data?.data?.hotelConfig) {
      // Initialize with current config or defaults
      const baseConfig = hotelConfigs?.[store?.id] || DEFAULT_HOTELOGIX_CONFIG;
      const defaultHotelConfig = {
        ...baseConfig,
        hotelId: hotelogixSetup?.data?.data?.hotelConfig?.hotelId || '',
        hotelName: hotelogixSetup?.data?.data?.hotelConfig?.hotelName || '',
        apiUsername: hotelogixSetup?.data?.data?.hotelConfig?.apiUsername || '',
        counterId: hotelogixSetup?.data?.data?.hotelConfig?.counterId || '',
        consumerKey: hotelogixSetup?.data?.data?.hotelConfig?.consumerKey || '',
        consumerSecret:
          hotelogixSetup?.data?.data?.hotelConfig?.consumerSecret || '',
        accessSecret:
          hotelogixSetup?.data?.data?.hotelConfig?.accessSecret || '',
        baseUrl:
          hotelogixSetup?.data?.data?.hotelConfig?.baseUrl ||
          'https://crs.hotelogix.net/ws/web/',
        redisHost:
          hotelogixSetup?.data?.data?.hotelConfig?.redisHost || 'localhost',
        redisPort: hotelogixSetup?.data?.data?.hotelConfig?.redisPort || 6379,
        isEnabled:
          hotelogixSetup?.data?.data?.hotelConfig?.isEnabled !== undefined
            ? hotelogixSetup?.data?.data?.hotelConfig?.isEnabled
            : true,
      };

      setInitialConfig(defaultHotelConfig);
      setHasUnsavedChanges(false);

      // Initialize in Redux if not already present or if data has changed
      if (!isSameHotelConfig(baseConfig, defaultHotelConfig)) {
        dispatch(
          updateHotelConfig({
            storeId: store?.id,
            config: defaultHotelConfig,
          })
        );
      }
    }
  }, [
    open,
    store?.id,
    dispatch,
    hotelogixSetup?.data?.data?.hotelConfig,
    hotelConfigs,
  ]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    dispatch(
      updateHotelConfig({
        storeId: store?.id,
        config: { [name]: fieldValue },
      })
    );

    setHasUnsavedChanges(true);

    // Clear errors for this field
    if (errors[`${store?.id}_${name}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${store?.id}_${name}`];
      dispatch(setErrors(newErrors));
    }
  };

  const handleCloseDrawer = () => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      dispatch(clearAllStates());
      dispatch(clearErrors());
      onClose();
    }
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);

      // Basic validation
      const requiredFields = [
        'hotelId',
        'apiUsername',
        'consumerKey',
        'consumerSecret',
      ];
      const newErrors: Record<string, string> = {};

      requiredFields.forEach(field => {
        const value =
          currentHotelConfig[field as keyof typeof currentHotelConfig];
        if (!value && value !== 0 && value !== false) {
          newErrors[`${store?.id}_${field}`] =
            `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        }
      });

      if (Object.keys(newErrors).length > 0) {
        dispatch(setErrors(newErrors));
        return;
      }

      // Update Redux state with the current hotel config
      dispatch(
        updateHotelConfig({
          storeId: store?.id,
          config: currentHotelConfig,
        })
      );

      // Create a system entry for this store if it doesn't exist
      const existingSystems = reservationSystemSetup.data.systems || [];
      const systemExists = existingSystems.find(
        sys => sys.storeId === store?.id
      );

      if (!systemExists) {
        dispatch(
          updateReservationSystemSetup({
            systems: [
              ...existingSystems,
              {
                restaurantId: currentHotelConfig.hotelId || '',
                storeId: store?.id,
                reservationSystem: 'hotelogix',
                storeAlias: store?.friendly_name,
              },
            ],
            selectedSystem: 'hotelogix',
          })
        );
      }

      // Save configuration using the reservation-system-setup section
      await saveAssistant({
        userId: user?._id,
        assistantId: agents?.agents[agents.agents.length - 1]._id,
        businessId: user?.business?._id,
        status: 'complete',
        section: 'reservation-system-setup',
      });

      showAlert('Hotel configuration saved successfully', 'success');
      dispatch(
        setStepStatus({
          step: 'reservationSystemSetup',
          status: { isComplete: true, hasError: false },
        })
      );

      // Update initial config to current saved state
      setInitialConfig(currentHotelConfig);
      setHasUnsavedChanges(false);
      setIsEditing(false);

      // Clear states after updating the initial config
      dispatch(clearAllStates());
      dispatch(clearErrors());

      // Close the drawer after everything is updated
      onClose();
    } catch (error) {
      console.error('Error saving hotel configuration:', error);
      showAlert(
        'Failed to save hotel configuration. Please try again.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = (field: string) => {
    switch (field) {
      case 'consumerKey':
        setShowApiKey(!showApiKey);
        break;
      case 'consumerSecret':
        setShowConsumerSecret(!showConsumerSecret);
        break;
      case 'accessSecret':
        setShowAccessSecret(!showAccessSecret);
        break;
    }
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
    // Reset to initial config
    dispatch(
      updateHotelConfig({
        storeId: store?.id,
        config: initialConfig,
      })
    );
    setHasUnsavedChanges(false);
    dispatch(clearAllStates());
    onClose();
  };

  const isConfigured = store?.is_hotel_configured || false;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        slotProps={{
          paper: {
            sx: {
              width: '500px',
              p: 3,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            },
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close (Cancel) Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={handleCloseDrawer}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <XIcon size={18} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: 'custom.textBlue',
                letterSpacing: '-0.2px',
              }}
            >
              {store.friendly_name} Hotel Configuration
            </Typography>
          </Box>

          {isEditing || !isConfigured ? (
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={!store || isSaving || isSubmitting}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
              onClick={handleSave}
            >
              {isSubmitting || isSaving ? 'Saving...' : 'Save'}
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 1,
            mx: -3,
          }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure your hotel system integration settings
        </Typography>

        {/* Configuration Fields */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Configuration
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0 }}
            >
              Hotel ID
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Hotel ID (e.g., 14095)"
              name="hotelId"
              size="small"
              value={currentHotelConfig?.hotelId || ''}
              onChange={handleChange}
              error={!!errors[`${store?.id}_hotelId`]}
              helperText={errors[`${store?.id}_hotelId`]}
              disabled={!isEditing && isConfigured}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0 }}
            >
              Hotel Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Hotel Name"
              name="hotelName"
              size="small"
              value={currentHotelConfig?.hotelName || ''}
              onChange={handleChange}
              error={!!errors[`${store?.id}_hotelName`]}
              helperText={errors[`${store?.id}_hotelName`]}
              disabled={!isEditing && isConfigured}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0 }}
            >
              API Username
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter API Username (e.g., api.user@hotelogix.com)"
              name="apiUsername"
              size="small"
              value={currentHotelConfig?.apiUsername || ''}
              onChange={handleChange}
              error={!!errors[`${store?.id}_apiUsername`]}
              helperText={errors[`${store?.id}_apiUsername`]}
              disabled={!isEditing && isConfigured}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0 }}
            >
              Counter ID
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Counter ID"
              name="counterId"
              size="small"
              value={currentHotelConfig?.counterId || ''}
              onChange={handleChange}
              error={!!errors[`${store?.id}_counterId`]}
              helperText={errors[`${store?.id}_counterId`]}
              disabled={!isEditing && isConfigured}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0 }}
            >
              Consumer Key
            </Typography>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Enter Consumer Key"
                name="consumerKey"
                size="small"
                type={showApiKey ? 'text' : 'password'}
                value={currentHotelConfig?.consumerKey || ''}
                onChange={handleChange}
                error={!!errors[`${store?.id}_consumerKey`]}
                helperText={errors[`${store?.id}_consumerKey`]}
                disabled={!isEditing && isConfigured}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '12px',
                  },
                  '& > div': {
                    height: '40px',
                  },
                }}
              />
              <IconButton
                onClick={() => togglePasswordVisibility('consumerKey')}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                }}
              >
                {showApiKey ? (
                  <EyeSlashIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0 }}
            >
              Consumer Secret
            </Typography>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Enter Consumer Secret"
                name="consumerSecret"
                size="small"
                type={showConsumerSecret ? 'text' : 'password'}
                value={currentHotelConfig?.consumerSecret || ''}
                onChange={handleChange}
                error={!!errors[`${store?.id}_consumerSecret`]}
                helperText={errors[`${store?.id}_consumerSecret`]}
                disabled={!isEditing && isConfigured}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '12px',
                  },
                  '& > div': {
                    height: '40px',
                  },
                }}
              />
              <IconButton
                onClick={() => togglePasswordVisibility('consumerSecret')}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                }}
              >
                {showConsumerSecret ? (
                  <EyeSlashIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0 }}
            >
              Base URL
            </Typography>
            <TextField
              fullWidth
              placeholder="https://crs.hotelogix.net/ws/web/"
              name="baseUrl"
              size="small"
              value={currentHotelConfig?.baseUrl || ''}
              onChange={handleChange}
              error={!!errors[`${store?.id}_baseUrl`]}
              helperText={errors[`${store?.id}_baseUrl`]}
              disabled={!isEditing && isConfigured}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>
        </Box>
      </Drawer>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showConfirmModal}
        onClose={handleConfirmModalClose}
        onConfirm={handleSave}
        isPending={isSaving || isSubmitting}
        title="Discard Changes"
        description="You have unsaved changes. Do you want to save them before closing?"
        confirmButtonText="Save"
        cancelButtonText="Discard"
        confirmButtonColor="success"
      />
      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default HotelConfigurationDrawer;
